/**
 * Money in: every credit the clinics earned, with the receipts and invoices it
 * settles.
 *
 * The engine matches each bank credit to the clinic's receipts -- a card day
 * at the agreed rates, a UPI batch, a direct UPI credit, a cheque -- and posts
 * it split: revenue at taxable value, output GST, and what the bank kept as
 * its charge. A cash deposit stays with the drawer's match and is mapped here
 * to the oldest cash receipts it carried. Anything the engine is not sure of
 * waits here, with the reason, for the Book Keeper.
 *
 * A Revenue-from-operations line reaches the books only through this screen:
 * Bank statements refuses to post one by category alone.
 */

import { Fragment, useEffect, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Loading, Note, Stat } from '../components/ui'
import { PAGE, Pager, usePaged } from '../components/Pager'

interface MatchedReceipt {
  billing_record_id: string
  business_date: string | null
  clinic_code: string
  patient_label: string | null
  method: string | null
  receipt_no: string | null
  amount_paise: number
  allocated_paise: number
  invoice_feed_id: string | null
  invoice_number: string | null
  //: The whole invoice, which can be more than this credit settled of it.
  invoice_total_paise: number | null
  //: The invoice's other payments, e.g. "cash ₹7,000.00 (THA/000140/R1)".
  paid_otherwise: string | null
}

interface MoneyInLine {
  bank_transaction_id: string
  txn_date: string
  amount_paise: number
  narration: string
  bank_account: string
  category: string | null
  state: string
  match_id: string | null
  channel: string | null
  clinic_code: string | null
  method: string | null
  gross_paise: number
  mdr_paise: number
  revenue_paise: number
  gst_paise: number
  advance_paise: number
  invoices: number
  problem: string | null
  //: A cash deposit: placed by the clinic drawer's deposit match, never here.
  cash_deposit: boolean
  receipts: MatchedReceipt[]
  //: Who paid, as the bank names them; null for card and UPI settlements.
  payer: string | null
}

interface InvoiceStatus {
  feed_id: string
  number: string | null
  business_date: string | null
  clinic_code: string
  patient_label: string | null
  total_paise: number
  paid_at_clinic_paise: number
  paid_by: string | null
  received_paise: number
  due_paise: number
  //: waiting | partly | received
  status: string
  reasons: string[]
  credits: string[]
}

interface InvoiceSections {
  start: string
  statement_through: string | null
  counts: Record<string, number>
  due_paise: Record<string, number>
  invoices: InvoiceStatus[]
}

interface MoneyInList {
  start: string
  lines: MoneyInLine[]
  counts: Record<string, number>
}

interface RunResult {
  posted: number
  proposed: number
  mapped_cash: number
  cash_posted: number
  credit_applied: number
  released: number
  reversed: number
  problems: string[]
}

const STATE_LABEL: Record<string, string> = {
  posted: 'posted',
  mapped: 'cash, mapped',
  proposed: 'needs a look',
  unmatched: 'not matched',
}
const STATE_CLASS: Record<string, string> = {
  posted: 'settled',
  mapped: 'approved',
  proposed: 'under_review',
  unmatched: 'open',
}
const CHANNEL_LABEL: Record<string, string> = {
  upi: 'UPI',
  debit_card: 'Debit card',
  credit_card: 'Credit card',
  card: 'Card (mixed day)',
  savein: 'SaveIn',
  cheque: 'Cheque',
  netbanking: 'Net banking',
  cash: 'Cash deposit',
}

/** What the credit search reads: what the bank wrote, the payer, the clinic,
 * the amount with or without commas, and each receipt, patient and invoice
 * the credit settles. */
function searchText(l: MoneyInLine): string {
  const amounts = [l.amount_paise, ...l.receipts.map((r) => r.amount_paise)].flatMap((p) => [
    formatInr(p),
    (p / 100).toFixed(2),
  ])
  return [
    l.txn_date,
    l.narration,
    l.payer,
    l.bank_account,
    l.category,
    l.clinic_code,
    l.channel && (CHANNEL_LABEL[l.channel] ?? l.channel),
    STATE_LABEL[l.state],
    l.problem,
    ...amounts,
    ...l.receipts.flatMap((r) => [r.receipt_no, r.patient_label, r.invoice_number]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

/** Every word typed must be somewhere on the credit. */
function matches(search: string) {
  const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean)
  return (l: MoneyInLine) => {
    if (words.length === 0) return true
    const text = searchText(l)
    return words.every((w) => text.includes(w))
  }
}

function pdfHref(txnId: string) {
  return `/api/v1/banking/transactions/${txnId}/invoices.pdf`
}

function Receipts({ line }: { line: MoneyInLine }) {
  if (line.receipts.length === 0) return <div className="faint">No receipts on this line yet.</div>
  return (
    <table>
      <thead>
        <tr>
          <th>Date</th>
          <th>Receipt</th>
          <th>Patient</th>
          <th>Method</th>
          <th>Invoice</th>
          <th className="num">Invoice total</th>
          <th className="num">Receipt amount</th>
          <th className="num">Settled by this credit</th>
        </tr>
      </thead>
      <tbody>
        {line.receipts.map((r) => (
          <tr key={r.billing_record_id}>
            <td className="date">{r.business_date ?? '—'}</td>
            <td className="mono">{r.receipt_no ?? '—'}</td>
            <td className="muted">{r.patient_label ?? '—'}</td>
            <td>{r.method ?? '—'}</td>
            <td>
              {r.invoice_feed_id ? (
                <a
                  href={`/api/v1/clinic-billing/invoices/${encodeURIComponent(r.invoice_feed_id)}.pdf`}
                >
                  {r.invoice_number ?? 'invoice'}
                </a>
              ) : (
                <span className="faint">advance, no bill</span>
              )}
              {r.paid_otherwise && (
                <div className="faint">rest paid otherwise: {r.paid_otherwise}</div>
              )}
            </td>
            <td className="num">
              {r.invoice_total_paise !== null ? formatInr(r.invoice_total_paise) : '—'}
            </td>
            <td className="num">{formatInr(r.amount_paise)}</td>
            <td className="num">{formatInr(r.allocated_paise)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={7} className="muted">
            {line.receipts.length} receipt(s) settled by this credit
            {line.mdr_paise > 0 && `, less ${formatInr(line.mdr_paise)} kept by the bank`}
            {line.mdr_paise < 0 && `, plus ${formatInr(-line.mdr_paise)} paid above them`} ={' '}
            {formatInr(line.amount_paise)} credited
          </td>
          <td className="num">
            <strong>{formatInr(line.gross_paise)}</strong>
          </td>
        </tr>
      </tfoot>
    </table>
  )
}

function MatchByHand({ line, onDone }: { line: MoneyInLine; onDone: () => void }) {
  //: Kept whole, so a receipt picked from one search stays picked through the next.
  const [picked, setPicked] = useState<Record<string, MatchedReceipt>>({})
  const [reason, setReason] = useState('')
  //: The credit pays only part of what is picked: one bill, two payers.
  const [part, setPart] = useState(false)
  //: A search reaches every open receipt and bill since the start, not only
  //: the weeks around the credit.
  const [search, setSearch] = useState('')
  const [term, setTerm] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim().length >= 2 ? search.trim() : ''), 300)
    return () => clearTimeout(timer)
  }, [search])
  const candidates = useQuery({
    queryKey: ['money-in', 'candidates', line.bank_transaction_id, term],
    queryFn: () =>
      api.get<MatchedReceipt[]>(
        `/money-in/${line.bank_transaction_id}/candidates` +
          (term ? `?q=${encodeURIComponent(term)}` : ''),
      ),
    placeholderData: keepPreviousData,
  })
  const match = useMutation({
    mutationFn: () =>
      api.post<MoneyInLine>(`/money-in/${line.bank_transaction_id}/match`, {
        billing_record_ids: Object.keys(picked),
        reason: reason.trim(),
        part_of_receipts: part && kept > 0,
      }),
    onSuccess: onDone,
  })
  const chosen = Object.values(picked)
  const total = chosen.reduce((s, c) => s + c.amount_paise - c.allocated_paise, 0)
  const kept = total - line.amount_paise
  const found = candidates.data ?? []
  // What is picked stays on top while a search shows other receipts.
  const rows = [
    ...chosen.filter((c) => !found.some((f) => f.billing_record_id === c.billing_record_id)),
    ...found,
  ]

  if (candidates.isLoading) return <Loading what="Finding open receipts" />
  return (
    <div style={{ marginTop: 8 }}>
      <ErrorNote error={candidates.error ?? match.error} />
      <div className="row" style={{ marginBottom: 8, alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Search patient, receipt or invoice no., amount…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search receipts"
          style={{ minWidth: 320 }}
        />
        <div className="shrink muted" style={{ fontSize: 13 }}>
          {candidates.isFetching
            ? 'Searching…'
            : term
              ? `${found.length} open receipt(s) and bill(s) match “${term}”, any date`
              : 'Open receipts from 30 days before this credit to 3 days after; search to look further'}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="empty">
          {term ? `Nothing open matches “${term}”.` : 'No open receipts around this credit.'}
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th />
              <th>Date</th>
              <th>Clinic</th>
              <th>Receipt</th>
              <th>Patient</th>
              <th>Method</th>
              <th>Invoice</th>
              <th className="num">Still open</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.billing_record_id}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Pick ${c.receipt_no ?? c.invoice_number ?? 'receipt'}`}
                    checked={!!picked[c.billing_record_id]}
                    onChange={(e) => {
                      const next = { ...picked }
                      if (e.target.checked) next[c.billing_record_id] = c
                      else delete next[c.billing_record_id]
                      setPicked(next)
                    }}
                  />
                </td>
                <td className="date">{c.business_date}</td>
                <td>{c.clinic_code}</td>
                <td className="mono">{c.receipt_no ?? '—'}</td>
                <td className="muted">{c.patient_label ?? '—'}</td>
                <td>{c.method}</td>
                <td>{c.invoice_number ?? (c.invoice_feed_id ? 'not arrived' : 'advance')}</td>
                <td className="num">{formatInr(c.amount_paise - c.allocated_paise)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="row" style={{ marginTop: 8, alignItems: 'center' }}>
        <div className="shrink muted">
          Picked {formatInr(total)} against a credit of {formatInr(line.amount_paise)}
          {chosen.length > 0 &&
            (kept > 0
              ? part
                ? ` — this credit takes ${formatInr(line.amount_paise)}; ${formatInr(kept)} stays open for the credit that pays the rest`
                : ` — the bank kept ${formatInr(kept)}, posted as its charge`
              : kept < 0
                ? ` — the bank paid ${formatInr(-kept)} more, posted as a rounding difference`
                : ' — exact')}
        </div>
        {kept > 0 && (
          <label className="appr-check shrink">
            <input type="checkbox" checked={part} onChange={(e) => setPart(e.target.checked)} />
            Another credit pays the rest: leave {formatInr(kept)} open
          </label>
        )}
        <input
          placeholder="Why these receipts (required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          style={{ minWidth: 260 }}
        />
        <div className="shrink">
          <button
            className="primary"
            disabled={chosen.length === 0 || reason.trim().length < 3 || match.isPending}
            onClick={() => match.mutate()}
          >
            {match.isPending ? 'Posting…' : 'Match and post'}
          </button>
        </div>
      </div>
    </div>
  )
}

type Tab = 'credits' | 'no-invoice' | 'waiting' | 'partly'

function InvoiceTable({ rows, partly }: { rows: InvoiceStatus[]; partly: boolean }) {
  const [clinic, setClinic] = useState('')
  const clinics = Array.from(new Set(rows.map((r) => r.clinic_code))).sort()
  const shown = rows.filter((r) => !clinic || r.clinic_code === clinic)
  const paged = usePaged(shown, clinic)
  if (rows.length === 0) return <div className="empty">Nothing here.</div>
  return (
    <>
      <div className="row" style={{ padding: '10px 16px', alignItems: 'center' }}>
        <div className="shrink">
          <select value={clinic} onChange={(e) => setClinic(e.target.value)} aria-label="Clinic">
            <option value="">Every clinic</option>
            {clinics.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="shrink muted" style={{ fontSize: 13 }}>
          {shown.length} invoice(s) · {formatInr(shown.reduce((t, r) => t + r.due_paise, 0))} still
          to come
          {shown.length > PAGE && ` · showing ${paged.range}`}
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Invoice</th>
            <th>Clinic</th>
            <th>Patient</th>
            <th className="num">Total</th>
            <th>Paid at the clinic</th>
            {partly && <th className="num">Received</th>}
            <th className="num">Still to come</th>
            <th>Why</th>
          </tr>
        </thead>
        <tbody>
          {paged.rows.map((r) => (
            <tr key={r.feed_id}>
              <td className="date">{r.business_date ?? '—'}</td>
              <td>
                <a href={`/api/v1/clinic-billing/invoices/${encodeURIComponent(r.feed_id)}.pdf`}>
                  {r.number ?? 'invoice'}
                </a>
              </td>
              <td>{r.clinic_code}</td>
              <td className="muted">{r.patient_label ?? '—'}</td>
              <td className="num">{formatInr(r.total_paise)}</td>
              <td className="muted">
                {r.paid_at_clinic_paise > 0
                  ? `${formatInr(r.paid_at_clinic_paise)} · ${r.paid_by ?? ''}`
                  : 'not paid'}
              </td>
              {partly && (
                <td className="num">
                  {formatInr(r.received_paise)}
                  {r.credits.map((c) => (
                    <div key={c} className="faint">
                      {c}
                    </div>
                  ))}
                </td>
              )}
              <td className="num">{formatInr(r.due_paise)}</td>
              <td className="muted">
                {r.reasons.map((x) => (
                  <div key={x}>{x}</div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} />
    </>
  )
}

export default function MoneyIn() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<Tab>('credits')
  const [state, setState] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const [byHand, setByHand] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const canReconcile = can('clinic.billing.reconcile')
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['money-in'] })

  const list = useQuery({
    queryKey: ['money-in', 'lines'],
    queryFn: () => api.get<MoneyInList>('/money-in'),
  })
  const noInvoice = useQuery({
    queryKey: ['money-in', 'without-invoices'],
    queryFn: () => api.get<MoneyInLine[]>('/money-in/without-invoices'),
  })
  const sections = useQuery({
    queryKey: ['money-in', 'invoices'],
    queryFn: () => api.get<InvoiceSections>('/money-in/invoices'),
  })
  const run = useMutation({
    mutationFn: () => api.post<RunResult>('/money-in/run'),
    onSuccess: refresh,
  })
  const confirm = useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      api.post<MoneyInLine>(`/money-in/${args.id}/confirm`, { reason: args.reason }),
    onSuccess: refresh,
  })
  const release = useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      api.post<MoneyInLine>(`/money-in/${args.id}/release`, { reason: args.reason }),
    onSuccess: refresh,
  })

  const data = list.data
  const found = matches(search)
  const lines = (data?.lines ?? []).filter((l) => (!state || l.state === state) && found(l))
  const allWithout = noInvoice.data ?? []
  const without = allWithout.filter(found)
  const pagedLines = usePaged(lines, `${state}|${search}`)
  const pagedWithout = usePaged(without, `without|${search}`)
  const searchBox = (
    <input
      type="search"
      placeholder="Search payer, receipt, patient, invoice, amount…"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      aria-label="Search credits"
      style={{ minWidth: 280 }}
    />
  )

  if (list.isLoading) return <Loading what="Matching money in to its invoices" />
  const counts = data?.counts ?? {}
  const sum = (s: string) =>
    (data?.lines ?? []).filter((l) => l.state === s).reduce((t, l) => t + l.amount_paise, 0)
  const invoices = sections.data?.invoices ?? []
  const waiting = invoices.filter((i) => i.status === 'waiting')
  const partly = invoices.filter((i) => i.status === 'partly')

  const tabs: { key: Tab; label: string; count: number; paise: number }[] = [
    { key: 'credits', label: 'Bank credits', count: data?.lines.length ?? 0, paise: 0 },
    {
      key: 'no-invoice',
      label: 'Money received, no invoice',
      count: allWithout.length,
      paise: allWithout.reduce((t, l) => t + l.amount_paise, 0),
    },
    {
      key: 'waiting',
      label: 'Invoices waiting for money',
      count: waiting.length,
      paise: sections.data?.due_paise.waiting ?? 0,
    },
    {
      key: 'partly',
      label: 'Partly received',
      count: partly.length,
      paise: sections.data?.due_paise.partly ?? 0,
    },
  ]

  function creditRows(rows: MoneyInLine[], showPayer: boolean) {
    if (rows.length === 0) return <div className="empty">Nothing here.</div>
    return (
      <table>
        <thead>
          <tr>
            <th>Bank date</th>
            <th>Credit</th>
            <th>Channel</th>
            <th>Clinic</th>
            <th className="num">Amount</th>
            <th className="num">Receipts</th>
            <th className="num">Bank kept</th>
            <th className="num">Invoices</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((l) => (
            <Fragment key={l.bank_transaction_id}>
              <tr>
                <td className="date">{l.txn_date}</td>
                <td className="muted">
                  <span className="mono">{l.narration}</span>
                  <div className="faint">
                    {l.bank_account}
                    {l.category && ` · ${l.category}`}
                    {showPayer && l.payer && ` · paid by ${l.payer}`}
                  </div>
                </td>
                <td>{l.channel ? (CHANNEL_LABEL[l.channel] ?? l.channel) : '—'}</td>
                <td>{l.clinic_code ?? '—'}</td>
                <td className="num">{formatInr(l.amount_paise)}</td>
                <td className="num">{l.receipts.length > 0 ? formatInr(l.gross_paise) : '—'}</td>
                <td className="num">
                  {l.receipts.length > 0 && l.mdr_paise ? formatInr(l.mdr_paise) : '—'}
                </td>
                <td className="num">{l.invoices || '—'}</td>
                <td>
                  <span className={`pill ${STATE_CLASS[l.state] ?? 'draft'}`}>
                    {STATE_LABEL[l.state] ?? l.state}
                  </span>
                  {l.method === 'manual' && <div className="faint">matched by hand</div>}
                  {l.problem && <div className="faint">{l.problem}</div>}
                  {l.cash_deposit && l.state === 'unmatched' && (
                    <div className="faint">
                      waits for the Clinic drawer to match it to a deposit
                    </div>
                  )}
                </td>
                <td className="num">
                  <div className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                    <button
                      className="small"
                      onClick={() =>
                        setOpen(open === l.bank_transaction_id ? null : l.bank_transaction_id)
                      }
                    >
                      {open === l.bank_transaction_id ? 'Close' : 'Receipts'}
                    </button>
                    {l.invoices > 0 && (
                      <a href={pdfHref(l.bank_transaction_id)} style={{ alignSelf: 'center' }}>
                        Invoices (PDF)
                      </a>
                    )}
                    {canReconcile && l.state === 'proposed' && l.receipts.length > 0 && (
                      <button
                        className="small"
                        disabled={confirm.isPending}
                        onClick={() => {
                          const reason = window.prompt(
                            `Post this ${formatInr(l.amount_paise)} credit against these receipts? ${l.problem ?? ''} Say why it is right.`,
                          )
                          if (reason && reason.trim().length >= 3)
                            confirm.mutate({ id: l.bank_transaction_id, reason: reason.trim() })
                        }}
                      >
                        Confirm
                      </button>
                    )}
                    {canReconcile &&
                      !l.cash_deposit &&
                      (l.state === 'proposed' || l.state === 'unmatched') && (
                        <button
                          className="small"
                          onClick={() => {
                            setOpen(l.bank_transaction_id)
                            setByHand(
                              byHand === l.bank_transaction_id ? null : l.bank_transaction_id,
                            )
                          }}
                        >
                          Match by hand
                        </button>
                      )}
                    {canReconcile && l.state === 'posted' && (
                      <button
                        className="small"
                        disabled={release.isPending}
                        onClick={() => {
                          const reason = window.prompt(
                            'Take this match apart? Its entry is reversed and the receipts are free again. Say why.',
                          )
                          if (reason && reason.trim().length >= 3)
                            release.mutate({ id: l.bank_transaction_id, reason: reason.trim() })
                        }}
                      >
                        Release
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              {open === l.bank_transaction_id && (
                <tr>
                  <td colSpan={10}>
                    {l.state === 'posted' && (
                      <div className="faint" style={{ marginBottom: 6 }}>
                        Revenue {formatInr(l.revenue_paise)} · GST {formatInr(l.gst_paise)}
                        {l.advance_paise > 0 && ` · advances ${formatInr(l.advance_paise)}`}
                        {l.mdr_paise > 0 && ` · bank’s charge ${formatInr(l.mdr_paise)}`}
                      </div>
                    )}
                    <Receipts line={l} />
                    {byHand === l.bank_transaction_id && canReconcile && (
                      <MatchByHand
                        line={l}
                        onDone={() => {
                          setByHand(null)
                          refresh()
                        }}
                      />
                    )}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    )
  }

  return (
    <>
      <ErrorNote
        error={
          list.error ??
          noInvoice.error ??
          sections.error ??
          run.error ??
          confirm.error ??
          release.error
        }
      />

      <Note>
        Every credit since {data?.start ?? '1 October'} that is the clinics’ money, matched to the
        receipts and invoices it settles. A matched credit is posted split: revenue at taxable value
        by what was sold, output GST, and the bank’s charge (MDR) as payment aggregator fees. A cash
        deposit stays posted by the drawer’s match; here it is mapped to the oldest cash receipts it
        carried. Revenue-from-operations lines post only from this screen.
      </Note>

      <div className="grid">
        <Stat
          label="Posted"
          value={String(counts.posted ?? 0)}
          note={`${formatInr(sum('posted'))} with its invoices`}
        />
        <Stat
          label="Needs a look"
          value={String(counts.proposed ?? 0)}
          note={`${formatInr(sum('proposed'))} — each one says why`}
        />
        <Stat
          label="Cash deposits mapped"
          value={String(counts.mapped ?? 0)}
          note={formatInr(sum('mapped'))}
        />
        <Stat
          label="Bank statement to"
          value={sections.data?.statement_through ?? '—'}
          note="money after this date is not on a statement yet"
        />
      </div>

      {canReconcile && (
        <div className="row" style={{ marginBottom: 12 }}>
          <div className="shrink">
            <button className="primary" disabled={run.isPending} onClick={() => run.mutate()}>
              {run.isPending ? 'Matching…' : 'Match now'}
            </button>
          </div>
          {run.data && (
            <div className="shrink muted" style={{ fontSize: 13 }}>
              {run.data.posted} posted · {run.data.proposed} for a look · {run.data.mapped_cash}{' '}
              cash deposits mapped · {run.data.cash_posted} cash receipts ·{' '}
              {run.data.credit_applied} credit applied
              {run.data.released > 0 && ` · ${run.data.released} released`}
              {run.data.problems.length > 0 && ` · ${run.data.problems.length} problem(s)`}
            </div>
          )}
        </div>
      )}
      {run.data && run.data.problems.length > 0 && (
        <Note kind="warn">
          {run.data.problems.slice(0, 8).map((p) => (
            <div key={p}>{p}</div>
          ))}
        </Note>
      )}

      <div className="row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {tabs.map((t) => (
          <div className="shrink" key={t.key}>
            <button
              className={tab === t.key ? 'primary' : undefined}
              aria-pressed={tab === t.key}
              onClick={() => setTab(t.key)}
            >
              {t.label} ({t.count}){t.paise > 0 && ` · ${formatInr(t.paise)}`}
            </button>
          </div>
        ))}
      </div>

      {tab === 'credits' && (
        <Card
          title="Bank credits"
          hint="one row per bank credit; open a row for its receipts and invoices"
          actions={
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              {searchBox}
              <select value={state} onChange={(e) => setState(e.target.value)} aria-label="Show">
                <option value="">All</option>
                <option value="proposed">Needs a look</option>
                <option value="posted">Posted</option>
                <option value="mapped">Cash, mapped</option>
                <option value="unmatched">Not matched</option>
              </select>
            </div>
          }
          tight
          wide
        >
          {(lines.length > PAGE || search.trim()) && (
            <div className="muted" style={{ padding: '10px 16px 0', fontSize: 13 }}>
              {search.trim() && `${lines.length} credit(s) match “${search.trim()}” · `}
              Showing {pagedLines.range} credit(s)
            </div>
          )}
          {creditRows(pagedLines.rows, false)}
          <Pager page={pagedLines.page} pages={pagedLines.pages} onPage={pagedLines.setPage} />
        </Card>
      )}

      {tab === 'no-invoice' && (
        <Card
          title="Money received, no invoice"
          hint="credits on the statement with no invoice mapped to them yet"
          actions={searchBox}
          tight
          wide
        >
          <Note>
            Each of these reached the bank and no clinic invoice is mapped to it. Use Match by hand
            to pick the receipts or the unpaid invoices it paid — anyone may have paid for a
            patient, so the payer’s name need not match. A credit that is not a patient’s money
            (interest, a refund, a transfer) leaves this list once it is categorised on Bank
            statements.
          </Note>
          {noInvoice.isLoading ? (
            <Loading what="Finding credits with no invoice" />
          ) : (
            <>
              {(without.length > PAGE || search.trim()) && (
                <div className="muted" style={{ padding: '0 16px', fontSize: 13 }}>
                  {search.trim() && `${without.length} credit(s) match “${search.trim()}” · `}
                  Showing {pagedWithout.range} credit(s)
                </div>
              )}
              {creditRows(pagedWithout.rows, true)}
              <Pager
                page={pagedWithout.page}
                pages={pagedWithout.pages}
                onPage={pagedWithout.setPage}
              />
            </>
          )}
        </Card>
      )}

      {tab === 'waiting' && (
        <Card
          title="Invoices waiting for money"
          hint="bills none of whose money has reached the bank yet"
          tight
          wide
        >
          {sections.isLoading ? (
            <Loading what="Checking every invoice" />
          ) : (
            <InvoiceTable rows={waiting} partly={false} />
          )}
        </Card>
      )}

      {tab === 'partly' && (
        <Card
          title="Partly received"
          hint="bills only part of whose money has reached the bank"
          tight
          wide
        >
          {sections.isLoading ? (
            <Loading what="Checking every invoice" />
          ) : (
            <InvoiceTable rows={partly} partly />
          )}
        </Card>
      )}
    </>
  )
}
